import { useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { fmt } from "@/data/catalog";
import { toast } from "sonner";

const appearance = {
  theme: "flat",
  variables: {
    colorPrimary: "#FBC4A8",
    colorBackground: "#ffffff",
    colorText: "#2C2B2C",
    colorDanger: "#C0392B",
    fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
    borderRadius: "12px",
    spacingUnit: "4px",
  },
  rules: {
    ".Input": { border: "1px solid #EADDD7", padding: "12px" },
    ".Input:focus": { border: "1px solid #FBC4A8", boxShadow: "0 0 0 2px rgba(251,196,168,0.3)" },
    ".Label": { color: "#6E6168", fontSize: "12px", fontWeight: "500" },
  },
};

function InnerForm({ grandTotal, onSuccess, onReady }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: { return_url: `${window.location.origin}/checkout/success?provider=stripe` },
    });
    if (error) {
      toast.error(error.message || "Payment could not be completed.");
      setBusy(false);
      return;
    }
    if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
      onSuccess(paymentIntent.id);
    } else {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="stripe-card-form">
      <PaymentElement options={{ layout: "tabs", defaultValues: { billingDetails: { address: { country: "AU" } } } }} onReady={onReady} />
      <button type="button" onClick={submit} disabled={!stripe || busy} data-testid="stripe-pay-now"
        className="flex w-full items-center justify-center gap-2 rounded-full bg-[#232222] py-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#FFFCFA] transition-all hover:bg-[#3a3636] hover:tracking-[0.2em] disabled:opacity-60">
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</> : <><Lock className="h-3.5 w-3.5" /> Pay now · {fmt(grandTotal)}</>}
      </button>
    </div>
  );
}

export function StripeCardSection({ publishableKey, formValid, buildPayload, grandTotal, discountCode, onReady }) {
  const stripePromise = useMemo(() => (publishableKey ? loadStripe(publishableKey) : null), [publishableKey]);
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const sig = `${grandTotal}|${discountCode || ""}`;
  const lastSig = useRef("");
  const creating = useRef(false);

  useEffect(() => {
    if (!formValid || !publishableKey) return;
    if (lastSig.current === sig && clientSecret) return;
    if (creating.current) return;
    creating.current = true;
    lastSig.current = sig; // claim immediately so rapid re-runs can't create a duplicate order
    let cancelled = false;
    setLoading(true);
    setErr("");
    api.post("/stripe/payment-intent", buildPayload())
      .then(({ data }) => {
        if (cancelled) return;
        setClientSecret(data.client_secret);
      })
      .catch(() => { if (!cancelled) { setErr("Could not load secure card fields."); lastSig.current = ""; } })
      .finally(() => { creating.current = false; if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValid, sig, publishableKey]);

  const onSuccess = (piId) => {
    window.location.href = `${window.location.origin}/checkout/success?session_id=${piId}&provider=stripe`;
  };

  if (!publishableKey) {
    return <p className="text-sm text-[#9A8E88]" data-testid="stripe-no-key">Card payment isn't configured yet.</p>;
  }
  if (!formValid) {
    return <p className="text-sm text-[#9A8E88]" data-testid="stripe-need-details">Complete your contact &amp; delivery details above to enter your card.</p>;
  }
  if (err) {
    return <p className="text-sm text-[#C0392B]" data-testid="stripe-error">{err}</p>;
  }
  if (loading || !clientSecret) {
    return <div className="flex items-center gap-2 text-sm text-[#9A8E88]" data-testid="stripe-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading secure card fields…</div>;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
      <InnerForm grandTotal={grandTotal} onSuccess={onSuccess} onReady={onReady} />
    </Elements>
  );
}
